import * as React from 'react';
import { Button, Accordion, Panel, Row, Col, Alert } from 'react-bootstrap';
import notify from '../../../../../shared/feedback/notifications';
import ImageProcessingList from '../list/ImageProcessingList';
import FailedOrderRowImagesSection from '../FailedOrderRowImagesSection';
import InProgressOrderRowImagesSection from '../InProgressOrderRowImagesSection';
import PhotographerUploadBoxes from '../../../../photographerupload/list/PhotographerUploadBoxes';
import { Translate } from '../../../../../shared/LanguageService';
import * as Features from '../../../../../shared/Features';
import { ImageType, IOrderRow } from '../Entities';
import { handleError } from '../../../../../shared/ErrorCatching';
import * as PhotoStatus from '../../../../../shared/PhotoStatus';
import ProductPropertiesIcons from '../../../../propertyIcons/ProductPropertiesIcons';
import SelectMultipleToolbox from '../SelectMultipleToolbox';
import Loader from 'react-loader';
import { UserRole } from '../../../../../shared/types';
import * as ProductProperty from '../../../../../shared/ProductProperties';

interface IAdditionalOrderRow {
    orderRowId: number;
    expectedQuantity: number;
    isUploadable: boolean;
    productName: string;
    productProperties: any[];
}

interface IProps {
    orderId: number;
    orderRow: IOrderRow;
    additionalItems: IAdditionalOrderRow[];
    user: any;
    showModalForImage: (
        id: number,
        imageType: number,
        idList: number[],
        canDelete: boolean
    ) => void;
    saveMetadata: (metadataList: any[]) => Promise<any>;
    uploadDone: () => void;
    onDelete: (photoId: number) => Promise<any>;
    deleteFailedUploadedPhoto: (photoUploadMetadataId: number) => void;
    photoStatus: any;
    downloadFile: (url: string, data?: any) => Promise<any>;
    ctrlIsDown: boolean;
    showCompareImages: boolean;
    setShowCompareImages: (value: boolean) => void;
    selectedImages: number[];
    setSelectedImages: (images: number[], tabKey: number) => void;
    setIsWriting: (value: boolean) => void;
    categories: any[];
    allowChangeCategories: boolean;
}

interface IState {
    upload: boolean;
    dragging: boolean;
    gatheringMetadata: boolean;
    totalBytes: number;
    uploadProgress: number;
    completedOrderRows: number[];
    progressObject: any;
    metadataList: any[];
    sendingMetadata: boolean;
    selectingMultiple: boolean;
    showReturnImageCommentSection: boolean;
    selectMultipleActionLoading: boolean;
}

const productsToExclude = [ProductProperty.BlueSky, ProductProperty.InteriorBlueSky];

class UneditedImagesRow extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.state = {
            upload: false,
            dragging: false,
            gatheringMetadata: false,
            totalBytes: 0,
            uploadProgress: 0,
            completedOrderRows: [],
            progressObject: {},
            metadataList: [],
            sendingMetadata: false,
            selectingMultiple: false,
            showReturnImageCommentSection: false,
            selectMultipleActionLoading: false
        };

        this.toggleDragging = this.toggleDragging.bind(this);
        this.updateTotalBytes = this.updateTotalBytes.bind(this);
        this.updateProgress = this.updateProgress.bind(this);
        this.updateTotalProgress = this.updateTotalProgress.bind(this);
        this.onUploadDone = this.onUploadDone.bind(this);
        this.setGatheringMetadata = this.setGatheringMetadata.bind(this);
        this.setOrderRowIdUploadDone = this.setOrderRowIdUploadDone.bind(this);
        this.addFileToMetadataList = this.addFileToMetadataList.bind(this);
        this.removeFileFromMetadataList = this.removeFileFromMetadataList.bind(this);
        this.onFailedImagesUploadDone = this.onFailedImagesUploadDone.bind(this);
        this.canAddFile = this.canAddFile.bind(this);
        this.canDeleteFile = this.canDeleteFile.bind(this);
        this.canUploadFile = this.canUploadFile.bind(this);
        this.isEditCustomerImage = this.isEditCustomerImage.bind(this);
    }

    toggleDragging(dragging: boolean) {
        this.setState({ dragging: dragging } as IState);
    }

    updateTotalBytes(bytes: number) {
        let totalBytes = this.state.totalBytes;
        totalBytes += bytes;
        this.setState({ totalBytes: totalBytes } as IState);
    }

    updateProgress(bytes: number, fileName: string, orderRowId: number, type: number) {
        let progressObject = this.state.progressObject;
        let orderRowProgress = progressObject[orderRowId.toString()];

        if (!orderRowProgress) {
            progressObject[orderRowId.toString()] = {};
            progressObject[orderRowId.toString()][type.toString()] = {};
        } else if (!orderRowProgress[type.toString()]) {
            progressObject[orderRowId.toString()][type.toString()] = {};
        }

        progressObject[orderRowId.toString()][type.toString()][fileName] = bytes;

        this.setState({ progressObject: progressObject } as IState);
        this.updateTotalProgress(progressObject);
    }

    updateTotalProgress(progressObject: any) {
        let progress = 0;

        for (let orderRow in progressObject) {
            for (let type in progressObject[orderRow]) {
                for (let file in progressObject[orderRow][type]) {
                    progress += progressObject[orderRow][type][file];
                }
            }
        }

        let progressPercentage = Math.max(
            parseInt(((progress / this.state.totalBytes) * 100).toFixed()),
            100
        );
        this.setState({ uploadProgress: progressPercentage } as IState);
    }

    onFailedImagesUploadDone() {
        this.props.uploadDone();
    }

    onUploadDone(anyUploads: boolean) {
        if (anyUploads) {
            this.props.uploadDone();
            notify.success(Translate('photographer-uploads/notification/upload-done'));
        }

        this.setState({ upload: false, uploadProgress: 0 } as IState);
    }

    setGatheringMetadata(gatheringMetadata: boolean) {
        this.setState({ gatheringMetadata: gatheringMetadata } as IState);
    }

    setOrderRowIdUploadDone(id: number) {
        let completedOrderRows = this.state.completedOrderRows;
        completedOrderRows.push(id);
        this.setState({ completedOrderRows: completedOrderRows } as IState);
    }

    addFileToMetadataList(
        orderRowId: number,
        category: number,
        fileName: string,
        imageWidth: number,
        imageHeight: number
    ) {
        let metadataList = this.state.metadataList;

        let orderRowMetadataIndex = metadataList.findIndex((x: any) => x.orderRowId === orderRowId);
        if (orderRowMetadataIndex > -1) {
            let categoryMetadataIndex = metadataList[
                orderRowMetadataIndex
            ].categoryMetadatas.findIndex((x: any) => x.category === category);
            if (categoryMetadataIndex > -1) {
                let fileMetadatas = metadataList[orderRowMetadataIndex].categoryMetadatas[
                    categoryMetadataIndex
                ].fileMetadatas.push({
                    filename: fileName,
                    width: imageWidth,
                    height: imageHeight
                });
            } else {
                let fileMetadata: any = {
                    filename: fileName,
                    height: imageHeight,
                    width: imageWidth
                };

                let categoryMetadata: any = {
                    category: category,
                    fileMetadatas: [fileMetadata]
                };

                metadataList[orderRowMetadataIndex].categoryMetadatas.push(categoryMetadata);
            }
        } else {
            let fileMetadata: any = {
                filename: fileName,
                height: imageHeight,
                width: imageWidth
            };

            let categoryMetadata: any = {
                category: category,
                fileMetadatas: [fileMetadata]
            };

            let metadataObj: any = {
                orderRowId: orderRowId,
                categoryMetadatas: [categoryMetadata]
            };

            metadataList.push(metadataObj);
        }

        this.setState({ metadataList: metadataList } as IState);
    }

    removeFileFromMetadataList(orderRowId: number, category: number, fileName: string) {
        let metadataList = this.state.metadataList;

        if (!metadataList || metadataList.length === 0) {
            return;
        }

        let orderRowIndex = metadataList.findIndex((x: any) => x.orderRowId === orderRowId);
        let categoryIndex = metadataList[orderRowIndex].categoryMetadatas.findIndex(
            (x: any) => x.category === category
        );
        if (categoryIndex === -1) {
            return;
        }
        let fileIndex = metadataList[orderRowIndex].categoryMetadatas[
            categoryIndex
        ].fileMetadatas.findIndex((x: any) => x.filename === fileName);

        metadataList[orderRowIndex].categoryMetadatas[categoryIndex].fileMetadatas = metadataList[
            orderRowIndex
        ].categoryMetadatas[categoryIndex].fileMetadatas.filter(
            (x: any) => x.filename !== fileName
        );

        if (
            metadataList[orderRowIndex].categoryMetadatas[categoryIndex].fileMetadatas.length === 0
        ) {
            metadataList[orderRowIndex].categoryMetadatas = metadataList[
                orderRowIndex
            ].categoryMetadatas.filter((x: any) => x.category !== category);
        }

        this.setState({ metadataList: metadataList } as IState);
    }

    startUpload() {
        this.setState({ sendingMetadata: true } as IState, () => {
            this.props
                .saveMetadata(this.state.metadataList)
                .then(() => {
                    this.setState({
                        upload: true,
                        sendingMetadata: false
                    } as IState);
                })
                .catch((error: any) => {
                    this.setState({ sendingMetadata: false } as IState);
                    handleError(error);
                });
        });
    }

    canAddFile(filename: string): boolean {
        return (
            !this.props.orderRow.fileNames.some((x) => x.name === filename) &&
            !this.props.orderRow.failedUploads.some((x) => x.filename === filename) &&
            !this.props.orderRow.uploadsInProgress.some((x) => x.filename === filename)
        );
    }

    canDeleteFile(): boolean {
        return (
            (Features.hasFeature(Features.DeleteUneditedImage, this.props.user) ||
                this.isEditCustomerImage()) &&
            (this.props.photoStatus.currentStatus >= PhotoStatus.UploaderApproved &&
            this.props.photoStatus.currentStatus < PhotoStatus.EditorApproved
                ? Features.hasFeature(Features.DeleteImagesAcceptedUpload, this.props.user)
                : true)
        );
    }

    canUploadFile() {
        return (
            ((Features.hasFeature(Features.UploadUneditedImage, this.props.user) ||
                this.isEditCustomerImage()) &&
                (this.props.photoStatus.currentStatus === PhotoStatus.Created ||
                    this.props.photoStatus.currentStatus === PhotoStatus.Uploading)) ||
            Features.hasFeature(Features.CanUploadImagesAnyTime, this.props.user)
        );
    }

    toggleSelectingMultiple = () => {
        if (this.state.selectingMultiple) {
            this.props.setSelectedImages([], 2);
        }

        this.setState(
            (prevState) =>
                ({
                    selectingMultiple: !prevState.selectingMultiple
                } as IState)
        );
    };

    selectAllImages = (thumbnails: IImageThumbnail[]) => {
        let imageIds = thumbnails.map((img) => img.id);
        if (imageIds.length === this.props.selectedImages.length) {
            this.props.setSelectedImages([], 2);
        } else {
            this.props.setSelectedImages([...imageIds], 2);
        }
    };

    imageSelected = (imageId: number, selected: boolean) => {
        if (selected) {
            this.props.setSelectedImages([...this.props.selectedImages, imageId], 2);
        } else {
            this.props.setSelectedImages(
                [...this.props.selectedImages.filter((id: number) => id !== imageId)],
                2
            );
        }
    };

    toggleReturnImageCommentSection = () => {
        if (!this.state.showReturnImageCommentSection) {
            this.props.setIsWriting(true);
        } else {
            this.props.setIsWriting(false);
        }
        this.setState(
            (prevState) =>
                ({
                    showReturnImageCommentSection: !prevState.showReturnImageCommentSection
                } as IState)
        );
    };

    setSelectMultipleActionLoading = (selectMultipleActionLoading: boolean) => {
        if (selectMultipleActionLoading === true) {
            this.setState({ selectMultipleActionLoading: true } as IState);
        } else {
            this.setState({
                selectMultipleActionLoading: false,
                selectingMultiple: false
            } as IState);
            this.props.setSelectedImages([], 2);
        }
    };

    renderHeader() {
        const { additionalItems } = this.props;
        const userRole = this.props.user.details.role;
        const selectingMultiple =
            this.state.selectingMultiple ||
            (this.props.selectedImages ? this.props.selectedImages.length > 0 : false);

        return (
            <>
                <div className="order-row-header">
                    <span className="product-name">{this.props.orderRow.name.trim()}</span>{' '}
                    <span className="number-of-images">
                        (
                        {Translate('unedited-images/number-of-images', {
                            numberOfImages: this.props.orderRow.numberOfImages
                        })}
                        )
                    </span>
                    <ProductPropertiesIcons
                        properties={
                            userRole == UserRole.Photographer
                                ? this.props.orderRow.orderRowProductProperties.filter(
                                      (property) => {
                                          return (
                                              property.value !==
                                              productsToExclude.find((x) => x === property.value)
                                          );
                                      }
                                  )
                                : this.props.orderRow.orderRowProductProperties
                        }
                    />
                    {additionalItems
                        ? additionalItems.map((x: any, i: number) => {
                              return (
                                  <span key={i}>
                                      <span> + </span>
                                      <span className="product-name">
                                          {x.productName.trim()}
                                      </span>{' '}
                                      <span className="number-of-images">
                                          (
                                          {Translate('unedited-images/number-of-images', {
                                              numberOfImages: x.expectedQuantity
                                          })}
                                          )
                                      </span>
                                  </span>
                              );
                          })
                        : null}
                    <br />
                    {this.props.orderRow.changeStatus.value !== 0 && (
                        <span className="orderrow-change-status-label">
                            {Translate(this.props.orderRow.changeStatus.displayName)}
                        </span>
                    )}
                </div>
                {Features.hasFeature(Features.ViewPhotographerInstructions, this.props.user) &&
                    this.props.orderRow.photographerInstructions && (
                        <Row className="order-row-instructions">
                            <Col>
                                <div>
                                    <Alert bsStyle="info" style={{ margin: 0 }}>
                                        <strong>
                                            {Translate(
                                                'photographer-uploads/order/row/instructions'
                                            )}
                                            :
                                        </strong>{' '}
                                        {additionalItems ? <br /> : null}
                                        {this.props.orderRow.photographerInstructions.trim() +
                                            ` (${this.props.orderRow.name.trim()})`}
                                        {additionalItems
                                            ? additionalItems.map((x: any, i: number) => {
                                                  return (
                                                      <>
                                                          <br />
                                                          <span>
                                                              {x.photographerInstructions.trim() +
                                                                  ` (${x.productName.trim()})`}
                                                          </span>
                                                      </>
                                                  );
                                              })
                                            : null}
                                    </Alert>
                                </div>
                            </Col>
                        </Row>
                    )}
                <SelectMultipleToolbox
                    user={this.props.user}
                    allowChangeCategories={this.props.allowChangeCategories}
                    categories={this.props.categories}
                    selectingMultiple={selectingMultiple}
                    toggleSelectingMultiple={this.toggleSelectingMultiple}
                    shouldShowAddedPhotos={true}
                    selectedImages={this.props.selectedImages}
                    allowStitching={false}
                    imageThumbnails={this.props.orderRow.thumbnails}
                    selectAllImages={this.selectAllImages}
                    canDelete={this.canDeleteFile}
                    toggleReturnImageCommentSection={this.toggleReturnImageCommentSection}
                    orderId={this.props.orderId}
                    setShowCompareImages={this.props.setShowCompareImages}
                    selectMultipleActionLoading={this.state.selectMultipleActionLoading}
                    setSelectMultipleActionLoading={this.setSelectMultipleActionLoading}
                    canDeletePhotos={true}
                    downloadFile={this.props.downloadFile}
                />
            </>
        );
    }

    isEditCustomerImage() {
        return (
            Features.hasFeature(Features.EditCustomerImages, this.props.user) &&
            this.props.photoStatus.hasEditCustomerImages
        );
    }

    renderFooter() {
        return (
            this.canUploadFile() && (
                <div>
                    {this.props.orderRow.uploadsInProgress.length > 0 && (
                        <InProgressOrderRowImagesSection
                            inProgressFiles={this.props.orderRow.uploadsInProgress}
                            orderRowId={this.props.orderRow.id}
                            orderId={this.props.orderId}
                        />
                    )}

                    {this.props.orderRow.failedUploads.length > 0 &&
                        (Features.hasFeature(Features.UploadFailedImages, this.props.user) ||
                            this.isEditCustomerImage()) && (
                            <FailedOrderRowImagesSection
                                failedFiles={this.props.orderRow.failedUploads}
                                orderRowId={this.props.orderRow.id}
                                orderId={this.props.orderId}
                                onUploadDone={this.onFailedImagesUploadDone}
                                onDelete={this.props.deleteFailedUploadedPhoto}
                            />
                        )}

                    <div className="photographer-upload-container">
                        {this.state.gatheringMetadata && (
                            <div className="orders loader-container">
                                <h3 className="loader-text">
                                    {Translate(
                                        'photographer-uploads/page/gathering-data-and-loading-preview'
                                    )}
                                </h3>
                                <Loader loaded={false} />
                            </div>
                        )}

                        <div
                            className={`upload-panel ${
                                this.state.gatheringMetadata ? 'waiting' : ''
                            }`}
                        >
                            <Accordion className="list-group order-list">
                                <PhotographerUploadBoxes
                                    orderId={this.props.orderId}
                                    orderRowId={this.props.orderRow.id}
                                    upload={this.state.upload}
                                    toggleDragging={this.toggleDragging}
                                    updateTotalBytes={this.updateTotalBytes}
                                    updateProgress={this.updateProgress}
                                    updateFilesAdded={() => {}}
                                    onUploadDone={this.onUploadDone}
                                    setGatheringMetadata={this.setGatheringMetadata}
                                    setOrderRowIdUploadDone={this.setOrderRowIdUploadDone}
                                    addFileToMetadataList={this.addFileToMetadataList}
                                    removeFileFromMetadataList={this.removeFileFromMetadataList}
                                    resetAfterComplete={true}
                                    canAddFile={this.canAddFile}
                                    hideNormalUploadBox={this.props.photoStatus.hasFlex}
                                    hideExtraUploadBox={this.isEditCustomerImage()}
                                    hideNoDeliveryUploadBox={
                                        this.isEditCustomerImage() || this.props.photoStatus.hasFlex
                                    }
                                    customExtraUploadBoxTitle={
                                        this.props.photoStatus.hasFlex
                                            ? 'photographer-uploads/order/row/dropzone-header/extra/flex'
                                            : null
                                    }
                                    isPhotographerUploadPage={false}
                                    uploadDNG={true}
                                />
                            </Accordion>
                        </div>
                    </div>

                    <Button
                        onClick={() => this.startUpload()}
                        bsSize="large"
                        bsStyle="info"
                        className="upload-button"
                        disabled={
                            this.state.upload ||
                            this.state.gatheringMetadata ||
                            this.state.sendingMetadata
                        }
                    >
                        <i className="material-icons">cloud_upload</i>{' '}
                        {Translate('photographer-uploads/page/button/upload')}
                    </Button>
                </div>
            )
        );
    }

    render() {
        return (
            <div className="panel-group">
                <Panel className="image-processing-panel">
                    <Panel.Heading>
                        <Panel.Title>{this.renderHeader()}</Panel.Title>
                    </Panel.Heading>
                    <Panel.Body>
                        <ImageProcessingList
                            imagesInProgress={this.props.orderRow.uploadsInProgress}
                            imageThumbnails={this.props.orderRow.thumbnails}
                            showModalForImage={this.props.showModalForImage}
                            imageType={ImageType.UNEDITED}
                            orderRowName={this.props.orderRow.name}
                            orderId={this.props.orderId}
                            orderRowId={this.props.orderRow.id}
                            key={this.props.orderRow.id}
                            numberOfPhotosInRow={null}
                            shouldShowAddedPhotos={true}
                            productProperties={this.props.orderRow.orderRowProductProperties}
                            allowStitching={false}
                            onDelete={this.props.onDelete}
                            canDelete={this.canDeleteFile}
                            allowChangeCategories={false}
                            selectedImages={this.props.selectedImages}
                            selectingMultiple={this.state.selectingMultiple}
                            imageSelected={this.imageSelected}
                            toggleReturnImageCommentSection={this.toggleReturnImageCommentSection}
                            showReturnImageCommentSection={this.state.showReturnImageCommentSection}
                            setShowCompareImages={this.props.setShowCompareImages}
                            showCompareImages={this.props.showCompareImages}
                            ctrlIsDown={this.props.ctrlIsDown}
                            selectMultipleActionLoading={this.state.selectMultipleActionLoading}
                            setSelectMultipleActionLoading={this.setSelectMultipleActionLoading}
                            editedTab={false}
                        />
                    </Panel.Body>
                    <Panel.Footer>{this.renderFooter()}</Panel.Footer>
                </Panel>
            </div>
        );
    }
}

export default UneditedImagesRow;
